import { SLoader } from '@strana-artw/ui-kit';

import { consoleError } from '@/shared/lib/utils/console';

import {
  badgePositions,
  positionList,
} from './notifyConstants';

export interface NotifMeta {
  timer?: ReturnType<typeof setTimeout>;
  group?: string;
  uid?: number;
  [key: string]: any;
}

export interface Notif {
  position: string;
  group?: unknown;
  onDismiss?: () => void;
  meta: NotifMeta;
  [key: string]: any;
}

export type NotifyConfig = Record<string, any>;

let uid = 0;

export const defaults: NotifyConfig = {};

export const groups: Record<string, Notif> = {};

export function createNotif (): Notif {
  return {
    textColor: 'white',
    position: '',
    meta: {},
  };
}

export function applyDefaults (notif: Notif, config: NotifyConfig): void {
  if (config.ignoreDefaults !== true) {
    Object.assign(notif, defaults);
  }
}

export function normalizeConfig (notif: Notif, config: NotifyConfig, originalApi: NotifyConfig): NotifyConfig {
  if (Object(config) === config) {
    return config;
  }

  if (notif.type) {
    Object.assign(notif, originalApi.types[notif.type]);
  }

  return { message: config };
}

export function resolveSpinner (notif: Notif): void {
  if (notif.spinner === undefined) {
    notif.spinner = false;
  } else if (notif.spinner === true) {
    notif.spinner = SLoader;
  }
}

export function resolvePosition (notif: Notif, config: NotifyConfig): boolean {
  if (notif.position) {
    if (positionList.includes(notif.position) === false) {
      consoleError('wrong position', config);

      return false;
    }

    return true;
  }

  notif.position = 'bottom';

  return true;
}

export function resolveTimeout (notif: Notif, config: NotifyConfig): boolean {
  if (notif.timeout === undefined) {
    notif.timeout = 5000;

    return true;
  }

  const timeout = Number.parseInt(notif.timeout, 10);

  if (Number.isNaN(timeout) || timeout < 0) {
    consoleError('wrong timeout', config);

    return false;
  }

  notif.timeout = timeout;

  return true;
}

export function resolveProgress (notif: Notif): void {
  if (notif.timeout === 0) {
    notif.progress = false;
  } else if (notif.progress === true) {
    notif.meta.progressStyle = {
      animationDuration: `${ notif.timeout + 1000 }ms`,
    };
  }
}

export function collectActions (
  notif: Notif,
  config: NotifyConfig,
  originalApi: NotifyConfig,
  dismiss: () => void,
): Array<Record<string, any>> {
  const actions = (
    Array.isArray(config.actions) === true ? config.actions : []
  )
    .concat(config.ignoreDefaults !== true
      && Array.isArray(defaults.actions) === true
      ? defaults.actions
      : [])
    .concat(originalApi.types[config.type] !== undefined
      && Array.isArray(originalApi.types[config.type].actions) === true
      ? originalApi.types[config.type].actions
      : []);

  if (notif.closeBtn) {
    actions.push({
      label:
        typeof notif.closeBtn === 'string'
          ? notif.closeBtn
          : 'Закрыть',
    });
  }

  return actions.map(({
    handler,
    noDismiss,
    style,
    class: klass,
    attrs,
    ...props
  }) => ({
    staticClass: klass,
    style,
    props: { flat: true, ...props },
    attrs,
    on: {
      click:
        typeof handler === 'function'
          ? () => {
              handler();

              if (noDismiss !== true) {
                dismiss();
              }
            }
          : () => {
              dismiss();
            },
    },
  }));
}

export function buildMetaClasses (notif: Notif): void {
  Object.assign(notif.meta, {
    staticClass:
      `v-notification row items-stretch`
      + ` v-notification--${
        notif.multiLine === true ? 'multi-line' : 'standard'
      }${
        notif.color !== undefined ? ` v-notification-${ notif.color }` : ''
      }${ notif.textColor !== undefined
        ? ` ${ notif.textColor }--text`
        : ''
      }${ notif.classes !== undefined ? ` ${ notif.classes }` : '' }`,
    wrapperClass:
      `v-notification__wrapper col ${
        notif.multiLine === true
          ? 'column no-wrap justify-center'
          : 'row items-center' }`,
    contentClass:
      `v-notification__content ${
        notif.multiLine === true ? '' : ' col' }`,
    attrs: {
      role: 'alert',
      ...notif.attrs,
    },
  });
}

export function resolveGroup (notif: Notif): void {
  if (notif.group === false) {
    notif.group = undefined;
    notif.meta.group = undefined;

    return;
  }

  if (notif.group === undefined || notif.group === true) {
    notif.group = [
      notif.message,
      notif.caption,
      notif.multiline,
    ]
      .concat(notif.actions.map((a: Record<string, any>) => `${ a.props.label }*${ a.props.icon }`))
      .join('|');
  }

  notif.meta.group = `${ notif.group }|${ notif.position }`;
}

export function finalizeActions (notif: Notif): void {
  if (notif.actions.length === 0) {
    notif.actions = undefined;

    return;
  }

  notif.meta.actionsClass
    = `v-notification__actions ${
      notif.multiLine === true ? 'justify-end' : 'col-auto'
    }${ notif.meta.hasMedia === true
      ? ' v-notification__actions--with-media'
      : '' }`;
}

export function insertFreshNotif (notifs: Record<string, Array<Notif>>, notif: Notif): void {
  notif.meta.uid = uid++;
  notif.meta.badge = 1;

  if (['left', 'right', 'center'].includes(notif.position)) {
    notifs[notif.position].splice(Math.floor(notifs[notif.position].length / 2), 0, notif);
  } else if (notif.position.includes('top')) {
    notifs[notif.position].unshift(notif);
  } else {
    notifs[notif.position].push(notif);
  }

  const groupKey = notif.meta.group;

  if (notif.group !== undefined && groupKey !== undefined) {
    groups[groupKey] = notif;
  }
}

export function updateGroupedNotif (
  notifs: Record<string, Array<Notif>>,
  notif: Notif,
  original: Notif,
  config: NotifyConfig,
): boolean {
  clearTimeout(original.meta.timer);

  if (notif.badgePosition === undefined) {
    notif.badgePosition = `top-${
      notif.position.includes('left')
        ? 'right'
        : 'left'
    }`;
  } else if (badgePositions.includes(notif.badgePosition) === false) {
    consoleError('wrong badgePosition', config);

    return false;
  }

  notif.meta.uid = original.meta.uid;
  notif.meta.badge = original.meta.badge + 1;
  notif.meta.badgeStaticClass
    = `v-notification__badge v-notification__badge--${ notif.badgePosition }${
      notif.badgeColor !== undefined
        ? ` ${ notif.badgeColor }`
        : ''
    }${ notif.badgeTextColor !== undefined
      ? ` ${ notif.badgeTextColor }--text`
      : '' }`;

  const index = notifs[notif.position].indexOf(original);
  const groupKey = notif.meta.group;

  if (groupKey !== undefined) {
    groups[groupKey] = notif;
  }

  notifs[notif.position][index] = notif;

  return true;
}
